import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  IconButton,
  InputAdornment,
  Paper,
  Link as MuiLink,
  Chip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useState } from "react";
import { selectUser } from "../../ducks/auth/selectors";
import TruckBanner from "./truck-banner.jpg";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift, faShare, faHeart } from "@fortawesome/free-solid-svg-icons";

type ReferralModalProps = {
  open: boolean;
  handleClose: () => void;
  fullPage?: boolean;
};

const ReferralModal: React.FC<ReferralModalProps> = ({ open, handleClose }) => {
  const user = useSelector(selectUser);
  const [copied, setCopied] = useState(false);
  if (!user) return null;

  const referrer = `${user.first}, ${user.company_name}`;
  const referralUrl = `https://doratms.com/henvisning/?message=${encodeURIComponent(referrer)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={handleClose}>
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={TruckBanner}
          sx={{
            width: "100%",
            height: 180,
            objectFit: "cover",
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        />

        <Chip
          label="Optjen et SuperGavekort på 2.000 kr."
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            backgroundColor: "#f5f5f5",
            fontWeight: 500,
          }}
        />

        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 12, right: 12 }}
          aria-label="luk"
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ px: 4, pt: 3, pb: 5 }}>
          <Typography variant="h1" sx={{ pb: 1 }}>
            Er du glad for DORA?
          </Typography>
          <Typography variant="h2" sx={{ pb: 2 }}>
            Henvis, og få 2.000 kr. i SuperGavekort
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            Sådan fungerer det:
          </Typography>
          <Stack spacing={1.5} mb={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FontAwesomeIcon icon={faShare} />
              <Typography variant="body1">
                Del dit link med andre vognmænd
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <FontAwesomeIcon icon={faHeart} />
              <Typography variant="body1">
                De tilmelder sig og får{" "}
                <Box component="span" fontWeight="600" display="inline">
                  1 uges gratis prøveperiode
                </Box>
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <FontAwesomeIcon icon={faGift} />
              <Typography variant="body1">
                Du får et{" "}
                <MuiLink
                  href="https://shop.gogift.com/da/dk/dkk"
                  target="_blank"
                  rel="noopener"
                  underline="always"
                  fontWeight="600"
                  display="inline"
                >
                  SuperGavekort på 2.000 kr.
                </MuiLink>{" "}
                for hver ny DORA-kunde*
              </Typography>
            </Stack>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Dit personlige link:
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                value={referralUrl}
                fullWidth
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip
                title="Kopieret!"
                open={copied}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <Button
                  onClick={handleCopy}
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                >
                  Kopiér
                </Button>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Eller del linket via:{" "}
              <MuiLink
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  referralUrl
                )}`}
                target="_blank"
                rel="noopener"
                underline="hover"
              >
                Facebook
              </MuiLink>{" "}
              |{" "}
              <MuiLink
                href={`mailto:?subject=Prøveperiode%20til%20DORA&body=Prøv%20DORA%20TMS%20i%207%20dage%20gratis:%20${encodeURIComponent(
                  referralUrl
                )}`}
                underline="hover"
              >
                Email
              </MuiLink>
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            *Din henvisning skal konvertere til betalende DORA-kunde ifm.
            prøveperioden.
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReferralModal;
